'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { pairKindOf } from '@cineamo/api/models/seat/seat-pairs';
import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import { IconBeanbag16 } from '@cineamo/ui/src/icons/ic-beanbag';
import { IconBed16 } from '@cineamo/ui/src/icons/ic-bed';
import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';
import { IconHeartSolid16 } from '@cineamo/ui/src/icons/ic-heartSolid';
import { IconWheelchair16 } from '@cineamo/ui/src/icons/ic-wheelchair';
import { DrawerOptions, HEX_COLOR } from '@cineamo/ui/src/seat-map/Drawer';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import { SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

/** The furniture a room has that the map marks with an icon: loveseats, beds, beanbags. */
export type SeatMapLegendFurniture = {
    love?: boolean;
    bed?: boolean;
    beanbag?: boolean;
};

type SeatMapLegendProps = {
    seatCategories: SeatCategory[];
    className?: string;
    renderSeatTypes: SeatMapLegendFurniture & {
        unavailable?: boolean;
        selected?: boolean;
        wheelchair?: boolean;
        dBox?: boolean;
        table?: boolean;
    };
    wrap?: boolean;
};

/** Which furniture icons the seats of a room show, for the legend. */
export function legendFurnitureOf(seats: { type: SeatType }[]): SeatMapLegendFurniture {
    return {
        love: seats.some((seat) => pairKindOf(seat.type) === 'loveseat'),
        bed: seats.some((seat) => pairKindOf(seat.type) === 'bed'),
        beanbag: seats.some((seat) => seat.type === SeatType.BEANBAG)
    };
}

const SeatMapLegend: React.FC<SeatMapLegendProps> = ({ seatCategories, className, wrap = false, renderSeatTypes }) => {
    const t = useTranslations('tickets');
    const seatColorsByCategory = useMemo<DrawerOptions['seatColorsByCategory']>(() => {
        const obj: Record<string, HEX_COLOR> = {};
        if (seatCategories) {
            seatCategories.forEach((category, index) => {
                obj[category.name ?? ''] = (category.color ?? seatColors[index % seatColors.length]) as HEX_COLOR;
            });
        }
        return obj;
    }, [seatCategories]);

    return (
        <div className={classnames('w-full flex flex-col gradient-mask-r-90', className)}>
            <ul
                className={classnames(
                    'flex overflow-x-auto gap-x-16 gap-y-8 lg:mx-auto no-scrollbar py-[4px] lg:py-0 pr-24',
                    wrap ? 'flex-wrap items-start content-start row-gap-8' : 'flex-nowrap'
                )}>
                {renderSeatTypes.unavailable && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4"
                            style={{ background: withOpacity(seatCategories[0]?.color ?? '', 0.5) ?? undefined }}>
                            <IconClose16 className="w-[80%] h-auto opacity-50" />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('unavailable')}</span>
                    </li>
                )}
                {renderSeatTypes.selected && (
                    <li className="flex flex-row items-center space-x-8">
                        <div className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4 bg-[var(--primary-color)]">
                            <div className="w-[50%] h-auto aspect-[1] bg-black rounded-full" />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('selected')}</span>
                    </li>
                )}
                {renderSeatTypes.wheelchair && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconWheelchair16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('wheelchair')}</span>
                    </li>
                )}
                {renderSeatTypes.love && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconHeartSolid16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('love-seat')}</span>
                    </li>
                )}
                {renderSeatTypes.bed && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconBed16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('bed')}</span>
                    </li>
                )}
                {renderSeatTypes.beanbag && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconBeanbag16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('beanbag')}</span>
                    </li>
                )}
                {renderSeatTypes.table && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className={classnames(
                                'size-[16px] lg:size-[20px] flex items-center justify-center rounded-2 lg:rounded-4',
                                'bg-[var(--on-background-color)]'
                            )}
                        />
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('table')}</span>
                    </li>
                )}
                {seatCategories.map((category) => (
                    <li
                        className="flex flex-row items-center space-x-8"
                        key={category.id}>
                        <div
                            className="size-[16px] lg:size-[20px] rounded-2 lg:rounded-4"
                            style={{ background: seatColorsByCategory[category.name] }}
                        />
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{category.name}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SeatMapLegend;
